import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState } from "react";

const CANCEL_REASON_OTHER_ID = "OTHER";
const MIN_OTHER_REASON_LENGTH = 20;

const cancelReasons: ISelectMenuItemData[] = [
  { id: "DOCTOR_DID_NOT_JOIN", title: "Doctor did not join" },
  { id: "DOCTOR_DID_NOT_FILL_EMR", title: "Doctor did not fill EMR" },
  { id: CANCEL_REASON_OTHER_ID, title: "Other" },
];

interface VideoConsultationCancelModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}

const VideoConsultationCancelModal: React.FC<VideoConsultationCancelModalProps> = ({ open, onClose, onSubmit }) => {
  const [selectedCancelReason, setSelectedCancelReason] = useState<ISelectMenuItemData | null>(null);
  const [otherCancelReason, setOtherCancelReason] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedCancelReason(null);
      setOtherCancelReason("");
    }
  }, [open]);

  const shouldShowOtherReasonField = selectedCancelReason?.id === CANCEL_REASON_OTHER_ID;
  const trimmedOtherCancelReason = otherCancelReason.trim();
  const isOtherReasonTooShort =
    shouldShowOtherReasonField &&
    trimmedOtherCancelReason.length > 0 &&
    trimmedOtherCancelReason.length < MIN_OTHER_REASON_LENGTH;

  const isCancelSubmitDisabled = useMemo(
    () =>
      !selectedCancelReason ||
      (shouldShowOtherReasonField && trimmedOtherCancelReason.length < MIN_OTHER_REASON_LENGTH),
    [selectedCancelReason, shouldShowOtherReasonField, trimmedOtherCancelReason.length],
  );

  const handleSubmit = () => {
    if (!selectedCancelReason) return;

    const exceptionReason =
      selectedCancelReason.id === CANCEL_REASON_OTHER_ID ? trimmedOtherCancelReason : selectedCancelReason.title;

    if (!exceptionReason) return;
    onSubmit(exceptionReason);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-xl transform rounded-lg border border-gray-200 bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:p-6">
                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  You are sure you want to cancel this video consultation.
                </Dialog.Title>

                <div className="mt-2 text-xs">
                  <p className="mb-2 text-xs font-medium text-[#1F1F23]">Select reason</p>
                  <SearchableSelectMenu
                    disableSearch
                    errorMessages={[]}
                    selectItems={cancelReasons}
                    defaultSelected={selectedCancelReason}
                    onSelectItem={(item) => {
                      setSelectedCancelReason(item);
                      if (item?.id !== CANCEL_REASON_OTHER_ID) {
                        setOtherCancelReason("");
                      }
                    }}
                    placeholder="Select reason"
                    className="text-xs"
                  />
                </div>

                {shouldShowOtherReasonField && (
                  <div className="mt-4 text-xs">
                    <label htmlFor="video-consultation-other-reason" className="mb-2 block text-xs font-medium text-[#1F1F23]">
                      Specify reason
                    </label>
                    <textarea
                      id="video-consultation-other-reason"
                      rows={3}
                      value={otherCancelReason}
                      onChange={(e) => setOtherCancelReason(e.target.value)}
                      placeholder="Type reason"
                      className="w-full rounded-md border border-gray-300 p-2 text-xs outline-none focus:border-gray-500"
                    />
                    {isOtherReasonTooShort && (
                      <p className="mt-1 text-xs text-red-500">
                        Please enter at least {MIN_OTHER_REASON_LENGTH} characters.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-5 flex items-center justify-end gap-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isCancelSubmitDisabled}
                    className="rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white disabled:bg-gray-400 hover:bg-red-500"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default VideoConsultationCancelModal;
